import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Check, Loader2 } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { useAlert } from "../../context/alert/AlertProvider";
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const stripePromise = loadStripe('pk_test_51QnF4rI18DTPnLzLj7JCcP5PxHpf0KsL7ZBDGIwSzskbAhoOoVGga0qUSmPcp1epTLKuioL247QCegf99m62Da4U00K1o8qdiz')

const MySubscriptionPage = () => {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState({});
    const [subscription, setSubscription] = useState(null);
    const [cancelingSubscription, setCancelingSubscription] = useState(false);
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const success = queryParams.get('success');
        const sessionId = queryParams.get('session_id');

        if (success === 'true' && sessionId) {
            navigate('/subscription')

            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login', {
                    state: {
                        message: 'Your subscription was successful! Please log in again.',
                        redirectTo: '/subscription'
                    }
                });
            } else {
                fetchSubscriptionDetails();
                showAlert({
                    title: t("subscription.success.title"),
                    description: t("subscription.success.description"),
                    variant: 'success'
                });
            }
        }

        fetchSubscriptionDetails();
    }, []);

    const fetchSubscriptionDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('https://www.ia-schedule.com:4139/api/stripe/subscription', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch subscription details');

            const data = await response.json();
            setSubscription(data);
        } catch (error) {
            console.error('Error fetching subscription details:', error);
        } finally {
            setInitialLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the current billing period.')) {
            return;
        }

        try {
            setCancelingSubscription(true);
            const token = localStorage.getItem('token');
            const response = await fetch('https://www.ia-schedule.com:4139/api/stripe/cancel-subscription', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to cancel subscription');

            await fetchSubscriptionDetails();
        } catch (error) {
            console.error('Error canceling subscription:', error);
            showAlert({
                title: t("alert.error"),
                description: t("alert.error.cancelSubscription"),
                variant: 'destructive'
            })
        } finally {
            setCancelingSubscription(false);
        }
    };

    const handleSubscribe = async (planName) => {
        try {
            setLoading(prev => ({ ...prev, [planName]: true }));
            const token = localStorage.getItem('token');
            const response = await fetch('https://www.ia-schedule.com:4139/api/stripe/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to create checkout session');

            const { sessionId } = await response.json();
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId });

            if (error) {
                console.error('Stripe error:', error);
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        } finally {
            setLoading(prev => ({ ...prev, [planName]: false }));
        }
    };

    const currentPlan = subscription?.status === 'active' ? t("subscription.pro.title") : t("subscription.free.title");

    const plans = [
        {
            name: t("subscription.free.title"),
            price: "0€",
            description: t("subscription.free.description"),
            features: [
                t("pricing.free.feature1"),
                t("pricing.free.feature2"),
                t("pricing.free.feature3"),
                t("pricing.free.feature4"),
                t("pricing.free.feature5")
            ]
        },
        {
            name: t("subscription.pro.title"),
            price: "9,99€",
            description: t("subscription.pro.description"),
            features: [
                t("pricing.pro.feature1"),
                t("pricing.pro.feature2"),
                t("pricing.pro.feature3"),
                t("pricing.pro.feature4"),
                t("pricing.pro.feature5"),
            ]
        }
    ];

    return (
        <div className="-mt-16 min-h-screen bg-gray-100 py-8 px-4">
            {initialLoading ? (
                <div className="flex justify-center items-center h-screen">
                    <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                </div>
            ) : (
                <div className="max-w-5xl mx-auto">
                    <Card className="mb-8">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                                <h2 className="text-xl sm:text-2xl font-bold">{t("subscription.current")}</h2>
                                <div className="flex items-center gap-4">
                                    <span className={`px-3 py-1 rounded-full text-sm ${subscription?.status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : subscription?.status === 'canceling'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {subscription?.status === 'active' ? t("subscription.active") :
                                            subscription?.status === 'canceling' ? t("subscription.cancelling") : t("subscription.free.title")}
                                    </span>
                                    {subscription?.status === 'active' && (
                                        <button
                                            onClick={handleCancelSubscription}
                                            disabled={cancelingSubscription}
                                            className="text-sm text-red-600 hover:text-red-700"
                                        >
                                            {cancelingSubscription ? (
                                                <span className="flex items-center">
                                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                                    {t("subscription.cancelling")}
                                                </span>
                                            ) : (
                                                t("subscription.cancel")
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-gray-500">{t("subscription.planName")}</p>
                                    <p className="font-semibold">{currentPlan}</p>
                                </div>
                                {subscription?.currentPeriodEnd && (
                                    <div>
                                        <p className="text-gray-500">
                                            {subscription?.cancelAtPeriodEnd ? t("subscription.ends") : t("subscription.nextBilling")}
                                        </p>
                                        <p className="font-semibold">
                                            {new Date(subscription.currentPeriodEnd).toLocaleDateString(localStorage.getItem('preferredLanguage'), {
                                                day: "2-digit",
                                                month: "long",
                                                year: "numeric"
                                            })}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {subscription?.cancelAtPeriodEnd && (
                                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                                    <p className="text-yellow-800">
                                        {t("subscription.cancel.message")}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        {plans.map((plan) => (
                            <Card
                                key={plan.name}
                                className={plan.name === currentPlan ? 'border-2 border-blue-500' : ''}
                            >
                                <CardContent className="p-4 sm:p-6">
                                    <div className="flex flex-col sm:flex-row justify-between mb-4 gap-3">
                                        <div>
                                            <h3 className="text-xl font-bold">{plan.name}</h3>
                                            <p className="text-gray-500">{plan.description}</p>
                                        </div>
                                        <div className="text-left sm:text-right">
                                            <p className="text-2xl font-bold">{plan.price}</p>
                                            {plan.price !== '0€' && (
                                                <p className="text-sm text-gray-500">/{t("subscription.month")}</p>
                                            )}
                                        </div>
                                    </div>

                                    <ul className="space-y-3 mb-6">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="flex items-start sm:items-center gap-2">
                                                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5 sm:mt-0" />
                                                <span className="text-sm sm:text-base">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => handleSubscribe(plan.name)}
                                        disabled={loading[plan.name] || plan.name === currentPlan}
                                        className={`w-full py-2 rounded-lg font-medium transition-colors
                                        ${loading[plan.name]
                                                ? 'bg-gray-300 cursor-not-allowed'
                                                : plan.name === currentPlan
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        {loading[plan.name] ? (
                                            <span className="flex items-center justify-center">
                                                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                                                Processing...
                                            </span>
                                        ) : plan.name === currentPlan ? (
                                            t("subscription.current")
                                        ) : plan.name === 'Free' ? (
                                            t("subscription.downgrade")
                                        ) : (
                                            t("subscription.upgrade")
                                        )}
                                    </button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default MySubscriptionPage;